/**
 * AuthContext - Shared authentication context for provider-agnostic auth state
 *
 * This context is populated by bridge components (MsalAuthBridge or KindeAuthBridge)
 * within the AuthProvider. It enables useAuth() to access the active provider's
 * state without needing to call provider-specific hooks directly.
 *
 * @module AuthContext
 * @see {@link AuthProvider} for context population
 * @see {@link useAuth} for context consumption
 */

import { createContext, useContext } from 'react'

/**
 * Context holding the unified auth interface from the active provider.
 *
 * Shape of context value (set by bridge components):
 * - provider: 'B2C' | 'Kinde'
 * - manager: useMsalManager() or useKindeManager() result
 * - isAuthenticated: boolean
 * - isLoading: boolean
 * - user: Object|null (provider-specific user object)
 */
export const AuthContext = createContext(null)

/**
 * Hook to consume the AuthContext.
 * Throws if used outside of AuthProvider.
 *
 * @returns {Object} The auth context value
 * @throws {Error} If used outside AuthProvider
 *
 * @example
 * // Internal use by useAuth hook
 * const authContext = useAuthContext()
 * const { isAuthenticated, manager } = authContext
 */
export const useAuthContext = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider')
    }
    return context
}
